<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { EntityAppearanceType, ProductFeature } from '@/api/generated/api.enum';
import ChevronDownIcon from '@/assets/icons/ChevronDownIcon.vue';
import { useCheckoutOfferSource } from '@/composables/billing/useCheckoutOfferSource';
import BillingCheckoutDialog from '@/core/dialogs/billing-checkout/BillingCheckoutDialog.vue';
import PositionablePopover from '@/core/dialogs/PositionablePopover.vue';
import { useDialog } from '@/core/dialogsystem/useDialog';
import { EKeyboardKey } from '@/utils/menu.utils';
import { computed, defineAsyncComponent, nextTick, useId, useTemplateRef, watch } from 'vue';
import { APPEARANCE_TABS } from './appearance.constants';
import AppearanceBackgroundPicker from './components/AppearanceBackgroundPicker.vue';
import AppearanceIconPanel from './components/AppearanceIconPanel.vue';
import AppearanceImagePanel from './components/AppearanceImagePanel.vue';
import EntityAppearance from './EntityAppearance.vue';
import type { TAppearanceSize, TAppearanceTab, TEntityAppearance } from './entityAppearance.types';
import { useAppearanceDraft } from '@/composables/useEntityAppearance';

const AppearanceEmojiPanel = defineAsyncComponent(() => import('./components/AppearanceEmojiPanel.vue'));
const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    modelValue: TEntityAppearance;
    abbreviation: string;
    label?: string;
    size?: TAppearanceSize;
    disabled?: boolean;
  }>(),
  { label: undefined, size: 'lg', disabled: false },
);
const emit = defineEmits<{
  'update:modelValue': [appearance: TEntityAppearance];
  preview: [appearance: TEntityAppearance];
  open: [];
  close: [];
}>();
const label = computed(() => props.label ?? t('entityAppearance.edit'));
const id = useId();
const anchor = useTemplateRef<HTMLButtonElement>('anchor');
const popover = useTemplateRef('popover');
const tabs = useTemplateRef<HTMLButtonElement[]>('tabs');
const { getFeatureGateOfferSource } = useCheckoutOfferSource();
const checkoutDialog = useDialog({ component: BillingCheckoutDialog, props: {} });

const requestImageUpgrade = (): void => {
  popover.value?.hide();
  checkoutDialog.show({
    feature: ProductFeature.tenantCustomization,
    offerSource: getFeatureGateOfferSource(ProductFeature.tenantCustomization),
  });
};
const {
  activeTab,
  backgroundColor,
  close,
  draft,
  image,
  isOpen,
  open,
  preview,
  removeImage,
  selectBackground,
  selectEmoji,
  selectIcon,
  selectImage,
  selectTab,
} = useAppearanceDraft(
  () => props.modelValue,
  (value) => emit('update:modelValue', value),
);

watch(preview, (value) => emit('preview', value), { immediate: true });

const toggle = async (): Promise<void> => {
  if (isOpen.value) {
    popover.value?.hide();
    return;
  }
  open();
  emit('open');
  await popover.value?.show();
  tabs.value?.[APPEARANCE_TABS.indexOf(activeTab.value)]?.focus();
};

const changeTab = async (tab: TAppearanceTab): Promise<void> => {
  selectTab(tab);
  await nextTick();
  popover.value?.updateDialogPosition();
};

const moveTab = async (event: KeyboardEvent): Promise<void> => {
  const index = APPEARANCE_TABS.indexOf(activeTab.value);
  let nextIndex: number;
  switch (event.key) {
    case EKeyboardKey.Left:
      nextIndex = (index + APPEARANCE_TABS.length - 1) % APPEARANCE_TABS.length;
      break;
    case EKeyboardKey.Right:
      nextIndex = (index + 1) % APPEARANCE_TABS.length;
      break;
    case EKeyboardKey.Home:
      nextIndex = 0;
      break;
    case EKeyboardKey.End:
      nextIndex = APPEARANCE_TABS.length - 1;
      break;
    default:
      return;
  }
  event.preventDefault();
  await changeTab(APPEARANCE_TABS[nextIndex]);
  tabs.value?.[nextIndex]?.focus();
};

const handleClose = (): void => {
  if (!isOpen.value) return;
  close();
  emit('close');
  // Preserve the destination focus on outside clicks; return keyboard focus after Escape.
  if (popover.value?.getDialogElement()?.contains(document.activeElement)) anchor.value?.focus();
};

defineExpose({ close: () => popover.value?.hide() });
</script>

<template>
  <span class="inline-flex">
    <button
      ref="anchor"
      type="button"
      class="inline-flex items-stretch rounded-md outline-inner disabled:cursor-default disabled:opacity-50"
      :aria-label="label"
      :aria-expanded="isOpen"
      aria-haspopup="dialog"
      :disabled="disabled"
      @click.stop="toggle"
      @keydown.esc.prevent="popover?.hide()"
    >
      <EntityAppearance :appearance="preview" :abbreviation="abbreviation" :size="size" class="!rounded-r-none" />
      <span class="-ml-px flex items-center rounded-r-md border border-divider bg-white px-xs"
        ><ChevronDownIcon class="size-icon-xs text-secondary"
      /></span>
    </button>
    <PositionablePopover
      ref="popover"
      scroll-mode="content"
      :anchor-element="anchor"
      :offset-y="8"
      :min-content-height="280"
      flip-strategy="both"
      mobile-drawer-mode
      :mobile-drawer-label="label"
      content-style="rounded-panel"
      dialog-style="!shadow-popup"
      role="dialog"
      :aria-label="label"
      @hide="handleClose"
    >
      <template #default="{ mobileDrawer }">
        <div
          v-if="isOpen"
          class="flex min-h-0 flex-col bg-white text-body font-normal leading-body tracking-normal"
          :class="mobileDrawer ? 'w-full flex-1' : 'w-selection-popover max-w-[calc(100vw-32px)]'"
        >
          <div
            class="grid h-cell shrink-0 grid-cols-4 border-b border-divider"
            role="tablist"
            :aria-label="label"
            @keydown="moveTab"
          >
            <button
              v-for="tab in APPEARANCE_TABS"
              :id="`${id}-${tab}`"
              :key="tab"
              ref="tabs"
              type="button"
              role="tab"
              class="relative pt-smd pb-sm text-body font-semibold leading-body text-secondary outline-inner hover:text-primary active:text-primary"
              :class="
                activeTab === tab &&
                '!text-primary after:absolute after:inset-x-0 after:-bottom-px after:h-[2px] after:bg-tint after:content-empty'
              "
              :aria-selected="activeTab === tab"
              :aria-controls="`${id}-panel`"
              :tabindex="activeTab === tab ? 0 : -1"
              @click="changeTab(tab)"
            >
              {{ t(`entityAppearance.${tab}`) }}
            </button>
          </div>
          <div
            :id="`${id}-panel`"
            role="tabpanel"
            :aria-labelledby="`${id}-${activeTab}`"
            class="flex min-h-0 flex-col"
            :class="mobileDrawer && activeTab !== EntityAppearanceType.text && 'h-[424px] max-h-[calc(100dvh-200px)]'"
          >
            <AppearanceEmojiPanel
              v-if="activeTab === EntityAppearanceType.emoji"
              :class="mobileDrawer && '!h-auto flex-1'"
              :selected="draft.type === EntityAppearanceType.emoji ? draft.key : null"
              @select="selectEmoji"
            />
            <AppearanceIconPanel
              v-else-if="activeTab === EntityAppearanceType.icon"
              :class="mobileDrawer && '!h-auto flex-1'"
              :selected="draft.type === EntityAppearanceType.icon ? draft.key : null"
              @select="selectIcon"
            />
            <AppearanceImagePanel
              v-else-if="activeTab === EntityAppearanceType.image"
              :class="mobileDrawer && 'h-full'"
              :source="image?.source"
              @select="selectImage"
              @remove="removeImage"
              @request-upgrade="requestImageUpgrade"
            />
            <AppearanceBackgroundPicker
              v-if="activeTab !== EntityAppearanceType.image"
              :model-value="backgroundColor"
              @update:model-value="selectBackground"
            />
          </div>
        </div>
      </template>
    </PositionablePopover>
  </span>
</template>
